import { useMutation, MutationResult, ExecutionResult } from 'react-apollo';
import { Enums } from '@letsdothis/tracking';

import { useAuthService } from '@/contexts/authService';
import getMutationUrl from '@/routes/routes/helpers/getMutationUrl';
import history from '@/routes/routes/helpers/history';
import { useTracking } from '@/tracking';
import { ActivityReaction, ActivityLike, ActivityType } from '@/types/user';
import { Logger } from '@utils/logger';
import { getRelativeUrl } from '@utils/routing';

import { LikeActivityMutation, UnlikeActivityMutation } from '../gql';
import { Activity } from '../types';
import useUser from './useUser';

type Success = { reactions: ActivityReaction };

const useHighFive = ({
  eventId,
  activity,
  activityType,
  contentCapturedContentType,
  // Used only for when url state doesn't encapsulate page state
  loginReturnUrl,
}: {
  eventId: string;
  activity: Activity;
  activityType: ActivityType;
  contentCapturedContentType: Enums.ContentCapturedContentType;
  loginReturnUrl?: string;
}): [
  {
    toggleHighFive: () => Promise<ExecutionResult<Success> | null | undefined>;
    highFives: ActivityLike[];
    previouslyHighFived: boolean;
  },
  MutationResult<Success>,
] => {
  const authService = useAuthService();
  const { user: userData } = useUser();
  const { contentCaptured, source: contentCapturedSource } = useTracking();

  const returnUrl =
    loginReturnUrl ?? getRelativeUrl(__CLIENT__ && window.location);
  const activityId = activity.id;
  const activityPrimaryIdentityId = activity?.user?.primaryIdentityId;
  const reactions = activity?.reactions;
  const pIID = userData?.primaryIdentityId;
  const hasPrimaryIdentityId = !!pIID;
  const highFives = reactions?.likes || [];
  const previouslyHighFived =
    hasPrimaryIdentityId &&
    !!highFives?.some((l) => l?.user?.primaryIdentityId === pIID);
  const [toggleLikeActivity, mutationState] = useMutation(
    previouslyHighFived ? UnlikeActivityMutation : LikeActivityMutation,
  );
  const isUserLoggedIn = authService.loggedIn();
  const loggedInUserId = authService.getUserCookie();
  const toggleHighFive = async () => {
    const variables = {
      activityId,
      activityType,
      activityPrimaryIdentityId,
      eventId,
    };

    if (!isUserLoggedIn || !loggedInUserId || !hasPrimaryIdentityId) {
      const mutatorVariables = {
        ...variables,
        returnUrl,
        contentCapturedContentType,
        contentCapturedSource,
      };
      history.replace(
        getMutationUrl(
          previouslyHighFived ? '/unlike-activity' : '/like-activity',
          mutatorVariables,
        ),
      );
      return;
    }
    const user = { ...userData, id: pIID };
    const reactionsId = `${activityType}::${activityId}`;
    const likes = (reactions?.likes || [])
      .concat({
        __typename: 'ActivityLike',
        id: `temp_${Date.now()}_${Math.random()}`,
        createdAt: new Date(),
        user,
      })
      .filter(
        (l) =>
          !previouslyHighFived ||
          l?.user?.primaryIdentityId !== userData?.primaryIdentityId,
      );

    try {
      const res = await toggleLikeActivity({
        variables,
        optimisticResponse: {
          __typename: 'Mutation',
          reactions: {
            id: reactionsId,
            __typename: 'ActivityReaction',
            comments: reactions?.comments || [],
            likes,
          },
        },
      });
      if (!previouslyHighFived) {
        contentCaptured(contentCapturedContentType);
      }

      return res;
    } catch (error) {
      Logger.error('[ERROR uploading new high five]: ', error);
      return null;
    }
  };

  return [{ toggleHighFive, previouslyHighFived, highFives }, mutationState];
};

export default useHighFive;
